import { useEffect } from "react";

interface ExtendedXMLHttpRequest extends XMLHttpRequest {
  requestUrl?: string;
  requestMethod?: string;
  requestStartTime?: number;
}

const env = process.env.NODE_ENV;
const allowedEnvs = ['development', 'qa'];
const isAllowedEnv = allowedEnvs.includes(env);

type DevelopmentOnlyCallback = (info: {
  url: string;
  status?: number;
  time: number;
  ok: boolean;
  error?: unknown;
  method?: string;
  payload?: any;
}) => void

export function useFetchInterceptor({ developmentOnlyCallback }: { developmentOnlyCallback: DevelopmentOnlyCallback }): void {
  if (!isAllowedEnv) return;
  // isAllowedEnv é estável e não muda entre reloads do app
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // Interceptar fetch
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const [url, options] = args;
      const start = performance.now();
      const urlString = String(url);
      const isRudderStack = urlString.includes('rudderstack.memed.com.br/v1/track');

      let payload;
      if (isRudderStack && options?.body) {
        try {
          payload = typeof options.body === 'string'
            ? JSON.parse(options.body)
            : options.body;
        } catch {
          payload = options.body;
        }
      }

      try {
        const response = await originalFetch(...args);
        const time = performance.now() - start;

        developmentOnlyCallback({
          url: urlString,
          status: response.status,
          ok: response.ok,
          time,
          method: 'fetch',
          ...(isRudderStack && payload && { payload }),
        });

        return response;
      } catch (error) {
        const time = performance.now() - start;

        developmentOnlyCallback({
          url: urlString,
          ok: false,
          time,
          error,
          method: 'fetch',
          ...(isRudderStack && payload && { payload }),
        });

        throw error;
      }
    };

    // Interceptar XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function openInterceptor(method: string, url: string | URL, ...rest: any[]): void {
      const extendedThis = this as ExtendedXMLHttpRequest;
      extendedThis.requestUrl = String(url);
      extendedThis.requestMethod = method;
      extendedThis.requestStartTime = performance.now();
      return originalXHROpen.apply(this, [method, url, ...rest] as any);
    };

    XMLHttpRequest.prototype.send = function sendInterceptor(body?: Document | XMLHttpRequestBodyInit | null): void {
      const extendedThis = this as ExtendedXMLHttpRequest;
      const { requestUrl: url, requestStartTime } = extendedThis;
      const start = requestStartTime || performance.now();
      const isRudderStack = url?.includes('rudderstack.memed.com.br/v1/track');

      let payload: any;
      if (isRudderStack && body) {
        try {
          payload = typeof body === 'string'
            ? JSON.parse(body)
            : body;
        } catch {
          payload = body;
        }
      }

      const onLoadEnd = (): void => {
        const { status } = extendedThis;
        const time = performance.now() - start;
        const ok = status >= 200 && status < 300;

        developmentOnlyCallback({
          url: url || 'unknown',
          status,
          ok,
          time,
          method: 'xhr',
          ...(isRudderStack && payload && { payload }),
        });

        extendedThis.removeEventListener('loadend', onLoadEnd);
        extendedThis.removeEventListener('error', onError);
      };

      const onError = (): void => {
        const time = performance.now() - start;

        developmentOnlyCallback({
          url: url || 'unknown',
          ok: false,
          time,
          error: 'XHR Error',
          method: 'xhr',
          ...(isRudderStack && payload && { payload }),
        });

        extendedThis.removeEventListener('loadend', onLoadEnd);
        extendedThis.removeEventListener('error', onError);
      };

      extendedThis.addEventListener('loadend', onLoadEnd);
      extendedThis.addEventListener('error', onError);

      return originalXHRSend.apply(this, body !== undefined ? [body] : []);
    };

    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
    };
  }, [developmentOnlyCallback]);
}
